import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { WelcomeModal, ShowWelcomeButton } from '../components/WelcomeModal';
import { 
    Coins, 
    UserPlus, 
    Gift, 
    TrendingUp,
    ChevronRight,
    Clock,
    CheckCircle2,
    XCircle,
    Phone,
    Mail
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
    const config = {
        new: { label: 'Neu', className: 'bg-blue-50 text-blue-700 border-blue-200', icon: Clock },
        contacted: { label: 'Kontaktiert', className: 'bg-amber-50 text-amber-700 border-amber-200', icon: Phone },
        qualified: { label: 'Qualifiziert', className: 'bg-purple-50 text-purple-700 border-purple-200', icon: CheckCircle2 },
        converted: { label: 'Konvertiert', className: 'bg-green-50 text-green-700 border-green-200', icon: TrendingUp },
        rejected: { label: 'Abgelehnt', className: 'bg-red-50 text-red-700 border-red-200', icon: XCircle }
    };
    
    const { label, className } = config[status] || config.new;
    
    return (
        <Badge variant="outline" className={className}>
            {label}
        </Badge>
    );
};

export const CustomerDashboard = () => {
    const { orgCode } = useParams();
    const { user, token } = useAuth();
    const { tenant } = useTenant();
    
    const [referrals, setReferrals] = useState([]);
    const [rewards, setRewards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showWelcome, setShowWelcome] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [referralsRes, rewardsRes] = await Promise.all([
                axios.get(`${API}/referrals/my`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API}/rewards`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setReferrals(referralsRes.data);
            setRewards(rewardsRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const convertedCount = referrals.filter(r => r.status === 'converted').length;
    const pendingCount = referrals.filter(r => !['converted', 'rejected'].includes(r.status)).length;

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    return (
        <DashboardLayout>
            {/* Welcome Modal */}
            <WelcomeModal 
                organization={tenant} 
                isOpen={showWelcome} 
                onClose={() => setShowWelcome(false)} 
            />

            <div className="space-y-8 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900">
                            Willkommen, {user?.name?.split(' ')[0]}!
                        </h1>
                        <p className="text-slate-600 mt-1">
                            Empfehlen Sie uns weiter und verdienen Sie Punkte
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <ShowWelcomeButton 
                            organization={tenant} 
                            onClick={() => {
                                // Clear the "seen" flag to show modal again
                                if (tenant?.id) {
                                    localStorage.removeItem(`welcome_seen_${tenant.id}`);
                                }
                                setShowWelcome(true);
                            }} 
                        />
                        <Link to={`/o/${orgCode}/submit-referral`}>
                            <Button 
                                size="lg"
                                style={{ backgroundColor: tenant?.primary_color }}
                                data-testid="add-lead-btn"
                            >
                                <UserPlus size={20} className="mr-2" />
                                Empfehlung eintragen
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Points Card - Prominent */}
                <Card 
                    className="overflow-hidden"
                    style={{ backgroundColor: tenant?.primary_color }}
                    data-testid="points-hero-card"
                >
                    <CardContent className="p-8">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                            <div className="text-white">
                                <p className="text-white/80 text-lg mb-2">Ihr Punktestand</p>
                                <p className="text-5xl font-bold">
                                    {user?.points_balance?.toLocaleString() || 0}
                                </p>
                                <p className="text-white/60 mt-2">Punkte</p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="bg-white/10 rounded-xl p-4 text-white text-center min-w-[120px]">
                                    <p className="text-2xl font-bold">{referrals.length}</p>
                                    <p className="text-white/80 text-sm">Empfehlungen gesamt</p>
                                </div>
                                <div className="bg-white/10 rounded-xl p-4 text-white text-center min-w-[120px]">
                                    <p className="text-2xl font-bold">{convertedCount}</p>
                                    <p className="text-white/80 text-sm">Konvertiert</p>
                                </div>
                                <div className="bg-white/10 rounded-xl p-4 text-white text-center min-w-[120px]">
                                    <p className="text-2xl font-bold">{pendingCount}</p>
                                    <p className="text-white/80 text-sm">In Bearbeitung</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* My Referrals List */}
                    <div className="lg:col-span-2">
                        <Card data-testid="my-leads-card">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="text-lg">Meine Empfehlungen</CardTitle>
                                {referrals.length > 0 && (
                                    <Badge variant="secondary">{referrals.length} Einträge</Badge>
                                )}
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="space-y-3">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />
                                        ))}
                                    </div>
                                ) : referrals.length === 0 ? (
                                    <div className="text-center py-12">
                                        <UserPlus size={48} className="mx-auto text-slate-300 mb-4" />
                                        <h3 className="text-lg font-medium text-slate-900 mb-2">
                                            Noch keine Empfehlungen eingetragen
                                        </h3>
                                        <p className="text-slate-500 mb-6 max-w-md mx-auto">
                                            Tragen Sie Ihre erste Empfehlung ein und verdienen Sie Punkte, 
                                            sobald diese konvertiert wird.
                                        </p>
                                        <Link to={`/o/${orgCode}/submit-referral`}>
                                            <Button 
                                                style={{ backgroundColor: tenant?.primary_color }}
                                                data-testid="first-lead-btn"
                                            >
                                                <UserPlus size={18} className="mr-2" />
                                                Erste Empfehlung eintragen
                                            </Button>
                                        </Link>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {referrals.map((referral) => (
                                            <div 
                                                key={referral.id} 
                                                className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                                                data-testid={`lead-item-${referral.id}`}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-1">
                                                        <p className="font-medium text-slate-900 truncate">
                                                            {referral.lead_name}
                                                        </p>
                                                        <StatusBadge status={referral.status} />
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-slate-500">
                                                        <span className="flex items-center gap-1">
                                                            <Mail size={14} />
                                                            {referral.lead_email}
                                                        </span>
                                                        <span className="hidden sm:flex items-center gap-1">
                                                            <Phone size={14} />
                                                            {referral.lead_phone}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-right text-sm text-slate-400 ml-4">
                                                    {formatDate(referral.created_at)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Incentives/Rewards */}
                    <div>
                        <Card data-testid="incentives-card">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="text-lg">Prämien</CardTitle>
                                <Link to={`/o/${orgCode}/rewards`}>
                                    <Button variant="ghost" size="sm" className="text-slate-600">
                                        Alle <ChevronRight size={16} className="ml-1" />
                                    </Button>
                                </Link>
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="space-y-3">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                                        ))}
                                    </div>
                                ) : rewards.length === 0 ? (
                                    <div className="text-center py-8">
                                        <Gift size={40} className="mx-auto text-slate-300 mb-3" />
                                        <p className="text-slate-500 text-sm">
                                            Noch keine Prämien verfügbar
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {rewards.slice(0, 5).map((reward) => {
                                            const canRedeem = (user?.points_balance || 0) >= reward.points_required;
                                            
                                            return (
                                                <div 
                                                    key={reward.id}
                                                    className={`p-4 rounded-xl border-2 transition-colors ${
                                                        canRedeem 
                                                            ? 'border-green-200 bg-green-50/50' 
                                                            : 'border-slate-100 bg-slate-50'
                                                    }`}
                                                    data-testid={`reward-item-${reward.id}`}
                                                >
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-slate-900 truncate">
                                                                {reward.title}
                                                            </p>
                                                            {reward.description && (
                                                                <p className="text-sm text-slate-500 truncate mt-0.5">
                                                                    {reward.description}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="text-right flex-shrink-0">
                                                            <p 
                                                                className="font-bold"
                                                                style={{ color: canRedeem ? '#16a34a' : tenant?.primary_color }}
                                                            >
                                                                {reward.points_required}
                                                            </p>
                                                            <p className="text-xs text-slate-500">Punkte</p>
                                                        </div>
                                                    </div>
                                                    {canRedeem && (
                                                        <div className="mt-2 flex items-center gap-1 text-green-600 text-sm">
                                                            <CheckCircle2 size={14} />
                                                            <span>Einlösbar</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Quick Info */}
                        <Card className="mt-6" data-testid="info-card">
                            <CardContent className="p-6">
                                <h4 className="font-medium text-slate-900 mb-3">So funktioniert's</h4>
                                <div className="space-y-3 text-sm text-slate-600">
                                    <div className="flex items-start gap-3">
                                        <div 
                                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                            style={{ backgroundColor: tenant?.primary_color }}
                                        >
                                            1
                                        </div>
                                        <p>Tragen Sie eine Empfehlung ein (Name, E-Mail, Telefon)</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div 
                                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                            style={{ backgroundColor: tenant?.primary_color }}
                                        >
                                            2
                                        </div>
                                        <p>Wir kontaktieren Ihre Empfehlung und bearbeiten sie</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div 
                                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                            style={{ backgroundColor: tenant?.primary_color }}
                                        >
                                            3
                                        </div>
                                        <p>Bei Konvertierung erhalten Sie <strong>250 Punkte</strong></p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};
